import classNames from 'classnames';
import _, { concat, filter, isNil, map, orderBy, reduce } from 'lodash';
import { forEach } from 'lodash/fp';
import { FunctionComponent, MouseEventHandler, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router';
import { Link } from 'react-router-dom';
import { Breadcrumb, BreadcrumbItem, Button, DropdownItem, DropdownMenu, DropdownToggle, Navbar, NavbarText, Table, UncontrolledButtonDropdown } from 'reactstrap';
import { Key } from 'readline';
import { gql, useQuery } from 'urql';
import { CenteredContainer, PrivateRoute } from '.';
import { useCurrentUser } from '../hooks';
import styled from '../themes/themed-styled-components';
import { DeepPartialGraph } from '../types';
import { HacpackQuery, HacpackQueryTerminalArguments, Handling, RiskAssessment, Terminal } from '../types/graph';
import { numericTierFromBrt } from '../util';
import DateTime from './DateTime';
import Loading from './Loading';
import LoadingUnitIdentifier from './LoadingUnitIdentifier';
import { GridLayout } from './ui';

// @ts-ignore
type TerminalAreasRiskQueryData = DeepPartialGraph<HacpackQuery>
type TerminalAreaRiskQueryArguments = HacpackQueryTerminalArguments;

const terminalSegregationQuery = gql<TerminalAreasRiskQueryData, TerminalAreaRiskQueryArguments>`
query terminal($id: Int!) {
    terminal (id: $id) {
        id
        name
        slug
        port
        feiLimit,
        ceiLimit,
        radiusLimit
        riskAssessments {
            id,
            area,
            fei,
            feiBay,
            feiRow,
            cei,
            ceiBay,
            ceiRow,
            radius,
            radiusBay,
            radiusRow
        }
        dataUpdate {
            id,
            lastUpdate
        }

        handlings(checkedIn:true) {
            id
            checkInDate
            loadingUnitIdentifier
            area
            bay
            row
            tier
            fei
            cei
            standardDistance
            dangerousGoods {
                id
                adrClass
                limitedQuantity
            }
        }
    }
}`;

const TerminalRiskTable = styled(Table)`
margin-bottom: 0;
background-color: #eee;
thead, tbody {
    td, th {
        text-align: center;
        vertical-align: middle;
    }
}
colgroup:nth-of-type(odd) col,
colgroup:nth-of-type(even) col:nth-child(even) {
    background-color: rgba(0,0,0,.1);
}
colgroup:nth-of-type(odd) col:nth-child(even) {
    background-color: transparent;
}
tbody > tr {

    &.active {
        background-color: rgba(255,255,255,.6);
    }

    //&:hover td,
    //&:hover th {
    //    background-color: rgba(0,0,25,.05);
    //}

    td[colspan="3"] {
        background-color: rgba(0,0,0,.2);
    }

    td.hoverable {
        cursor:pointer;
    }
    td.hover {
        background-color: rgba(200,200,255,.25);
    }
    td.active {
        background-color: rgba(255,255,255,.8);
    }
    td.active.hover {
        background-color: rgba(225,225,255,.8);
    }
}
thead {
    position: sticky;
    top: 0;
    th {
        background-color: #eee;
    }
    ::before {
        content: "";
        position: absolute;
        background-color: #ddd;
        z-index: -1;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }
}
tfoot {
    position: sticky;
    bottom: 0;
    background-color: white;
    text-align: center;
}
`;

const UnitsRiskTable = styled(Table)`
margin-bottom: 0;
thead, tbody {
    td, th {
        text-align: center;
        vertical-align: middle;
    }

    th {
        user-select: none;
        a {
            user-select: none;
            cursor: pointer;
        }
    }
    
}
tbody > tr {
    cursor:pointer;

    &:hover td,
    &:hover th {
        background-color: rgba(0,0,25,.05);
    }
}
`;

const PositionBreadcrumb = styled(Breadcrumb)`
> .breadcrumb {
    margin-bottom: 0;
    border-radius: 0;
    > .breadcrumb-item .btn {
        padding-top: 0;
        padding-bottom: 0;
        border-radius: 0;
    }
}
`;

type TerminalAreasRiskRowAreaRowClickHandler = (area: string, bay?: string | null, row?: string | null) => void

type TerminalAreasRiskRowProps = {
    // @ts-ignore
    riskAssessment: DeepPartialGraph<RiskAssessment>,
    active: boolean,
    activeRow: string | null,
    activeBay: string | null,
    feiLimit?: number | null,
    ceiLimit?: number | null,
    radiusLimit?: number | null,
    onRowClick?: TerminalAreasRiskRowAreaRowClickHandler
}

const AreaRiskRow: FunctionComponent<TerminalAreasRiskRowProps> = ({ riskAssessment: { id, area, fei, feiBay, feiRow, cei, ceiBay,
    ceiRow, radius, radiusBay, radiusRow }, active, activeRow, activeBay, onRowClick, feiLimit, ceiLimit, radiusLimit }) => {

    const { t } = useTranslation('common');
    const { isSuperuser } = useCurrentUser();
    const [hover, setHover] = useState<'fei' | 'cei' | 'radius' | null>(null);

    const setFeiHover = useCallback(() => setHover('fei'), [setHover]);
    const setCeiHover = useCallback(() => setHover('cei'), [setHover]);
    const setRadiusHover = useCallback(() => setHover('radius'), [setHover]);
    const setNullHover = useCallback(() => setHover(null), [setHover]);

    const feiHover = hover === 'fei';
    const ceiHover = hover === 'cei';
    const radiusHover = hover === 'radius';

    const handleRowClick: MouseEventHandler<HTMLElement> = useCallback(() => {
        if (area === undefined || area === null) {
            return;
        }

        switch (hover) {
            case 'fei':
                onRowClick?.(area, feiBay, feiRow);
                break;
            case 'cei':
                onRowClick?.(area, ceiBay, ceiRow);
                break;
            case 'radius':
                onRowClick?.(area, radiusBay, radiusRow);
                break;
        }
    }, [hover, area, feiBay, feiRow, ceiBay, ceiRow, radiusBay, radiusRow, onRowClick]);

    const feiCells = useMemo(() => {
        if (fei === null) {
            return <td colSpan={3}>{t("N.R.")}</td>;
        }

        const className = classNames({
            active: active === true && feiBay === activeBay && feiRow === activeRow,
            hover: feiHover,
            hoverable: true
        });

        return <>
            <td key="val" className={className} onMouseEnter={setFeiHover} onMouseLeave={setNullHover}><RiskValue value={fei} limit={feiLimit} /></td>
            <td key="bay" className={className} onMouseEnter={setFeiHover} onMouseLeave={setNullHover}>{feiBay ?? t("N.R.")}</td>
            <td key="row" className={className} onMouseEnter={setFeiHover} onMouseLeave={setNullHover}>{feiRow ?? t("N.R.")}</td>
        </>;
    }, [fei, feiBay, feiRow, setFeiHover, setNullHover, feiHover, t, active, activeBay, activeRow, feiLimit]);

    const ceiCells = useMemo(() => {
        if (cei === null) {
            return <td colSpan={3}>{t("N.R.")}</td>;
        }

        const className = classNames({
            active: active === true && ceiBay === activeBay && ceiRow === activeRow,
            hover: ceiHover,
            hoverable: true
        });

        return <>
            <td key="val" className={className} onMouseEnter={setCeiHover} onMouseLeave={setNullHover}><RiskValue value={cei} limit={ceiLimit} /></td>
            <td key="bay" className={className} onMouseEnter={setCeiHover} onMouseLeave={setNullHover}>{ceiBay ?? t("N.R.")}</td>
            <td key="row" className={className} onMouseEnter={setCeiHover} onMouseLeave={setNullHover}>{ceiRow ?? t("N.R.")}</td>
        </>;
    }, [cei, ceiBay, ceiRow, setCeiHover, setNullHover, ceiHover, t, active, activeBay, activeRow, ceiLimit]);

    const radiusCells = useMemo(() => {
        if (radius === null) {
            return <td colSpan={3}>{t("N.R.")}</td>;
        }

        const className = classNames({
            active: active === true && radiusBay === activeBay && radiusRow === activeRow,
            hover: radiusHover,
            hoverable: true
        });

        return <>
            <td key="val" className={className} onMouseEnter={setRadiusHover} onMouseLeave={setNullHover}><RiskValue value={radius} limit={radiusLimit} format={(v) => `${v}m`} /></td>
            <td key="bay" className={className} onMouseEnter={setRadiusHover} onMouseLeave={setNullHover}>{radiusBay ?? t("N.R.")}</td>
            <td key="row" className={className} onMouseEnter={setRadiusHover} onMouseLeave={setNullHover}>{radiusRow ?? t("N.R.")}</td>
        </>;
    }, [radius, radiusBay, radiusRow, setRadiusHover, setNullHover, radiusHover, t, active, activeBay, activeRow, radiusLimit]);



    return <tr key={id} onClick={handleRowClick}>
        {isSuperuser ? <th scope="row">{id}</th> : null}
        <th scope="row">{area}</th>

        {feiCells}
        {ceiCells}
        {radiusCells}
    </tr>;
};

type RiskValueProps = {
    value?: number | null,
    limit?: number | null,
    format?: (value: number) => ReactElement<any, any> | string,
    empty?: ReactElement<any, any> | string | null
}

const RiskValue: FunctionComponent<RiskValueProps> = ({ value, limit, format, empty }) => {
    if (value === undefined || value === null) {
        if (empty === undefined) {
            return <>-</>
        }
        return <>{empty ?? null}</>;
    }

    const formatted = format?.(value) ?? value.toString();

    if (value <= (limit ?? Infinity)) {
        return <>{formatted}</>;
    }

    return <strong>{formatted}</strong>;
}

type TerminalAreasRiskProps = {
    id: number,

}

const REFRESH_TIME = 3 * 60 * 1000;
const EXPIRED_REFRESH_TIME = 30 * 1000;

type HandlingsOrderBy = [keyof Handling | 'risk' | 'position', 'asc' | 'desc']
const HANDLINGS_ORDER_BY_STORAGE_KEY = 'terminalRiskHandlingsOrderBy';
const getStoredHandlingsOrderBy = () => (localStorage?.getItem(HANDLINGS_ORDER_BY_STORAGE_KEY)?.split(',') as HandlingsOrderBy) ?? ['risk', 'desc']
const storeHandlingsOrderBy = (handlingsOrderBy: HandlingsOrderBy) => localStorage?.setItem(HANDLINGS_ORDER_BY_STORAGE_KEY, handlingsOrderBy.join(','))

const TerminalAreasRisk: FunctionComponent<TerminalAreasRiskProps> = ({ id }) => {

    const [result, reexecuteQuery] = useQuery<TerminalAreasRiskQueryData, TerminalAreaRiskQueryArguments>({
        query: terminalSegregationQuery,
        variables: { id },
        requestPolicy: 'cache-and-network'
    });

    const { data, fetching, /*error*/ } = result;

    const [handlingsOrderBy, setHandlingsOrderBy] = useState<HandlingsOrderBy>(getStoredHandlingsOrderBy());

    useEffect(() => storeHandlingsOrderBy(handlingsOrderBy), [handlingsOrderBy])

    const terminal = data?.terminal;

    const refresh = useCallback(() => {
        reexecuteQuery(/*{ requestPolicy: 'cache-and-network' }*/);
    }, [reexecuteQuery]);

    const expiredDataUpdate = useRef<string | null>(null);

    const { t } = useTranslation('common');
    const { isSuperuser } = useCurrentUser();

    const { state: { parentUrl } } = useLocation<{ parentUrl: string }>();
    const { area: activeArea, bay: activeBay, row: activeRow } = useParams<{ area?: string, bay?: string, row?: string }>();
    const history = useHistory();

    const feiLimit = terminal?.feiLimit;
    const ceiLimit = terminal?.ceiLimit;
    const radiusLimit = terminal?.radiusLimit;

    const dataUpdate = terminal?.dataUpdate;
    //const lastUpdate = useRef<string|null>(dataUpdate?.lastUpdate??null);

    const riskAssessments = terminal?.riskAssessments;

    const limits: Partial<Record<'fei' | 'cei' | 'standardDistance', number>> = useMemo(() => reduce({ fei: feiLimit, cei: ceiLimit, standardDistance: radiusLimit }, (limits, limit, key) => {
        if (limit === undefined || limit === null) {
            return limits;
        }

        return { [key]: limit, ...limits };
    }, {}), [feiLimit, ceiLimit, radiusLimit]);

    const handlingsOrderTuple = useMemo(() => {
        const [column, direction] = handlingsOrderBy;

        switch (column) {
            case 'risk':
                return [[(handling: Handling) => reduce(limits, (result, limit, key) => {
                    const value = (handling as any)[key] as number | null | undefined;

                    if (value === undefined || value === null) {
                        return result;
                    }

                    const ratio = value / (limit as number);

                    if (ratio > 1) {
                        return result + ratio;
                    }

                    return result + (ratio * 0.1);
                }, 0)], [direction]]
            case 'position':
                return [['area', 'bay', 'row', 'tier'], [direction, direction, direction, direction]]
            case 'fei':
            case 'cei':
            case 'standardDistance':
                return [[(handling: Handling) => handling[column] ?? -1, 0], [direction]]
            default:
                return [[column], [direction]];
        }

    }, [handlingsOrderBy, limits])

    const handlings = useMemo(() => orderBy(terminal?.handlings, ...handlingsOrderTuple), [terminal, handlingsOrderTuple]);

    const terminalSlug = terminal?.slug;

    const areas = useMemo(() => _(handlings).filter(({ area }) => area !== null && area !== '').map('area').uniq().sort().value() as string[], [handlings]);
    const bays = useMemo(() => _(handlings).filter(({ area, bay }) => area === activeArea && bay !== null && bay !== '').map('bay').uniq().sort().value(), [handlings, activeArea]);
    const rows = useMemo(() => _(handlings).filter({ area: activeArea, bay: activeBay }).map('row').uniq().sort().value(), [handlings, activeBay, activeArea]);

    const areaSelector = useMemo(() => {
        const items = _(areas).filter((area) => area !== activeArea).map((area) => <DropdownItem key={area} tag={Link} to={`${parentUrl}/${area}`}>{area}</DropdownItem>).value();

        const value = activeArea === undefined ? t('Area') : <Trans t={t} i18nKey="areaSelector">Area <strong>{activeArea}</strong></Trans>

        if (items.length === 0) {
            return activeArea === undefined ? null : <Button color="link">{value}</Button>;
        }

        return <UncontrolledButtonDropdown color="link">
            {activeArea === undefined
                ? <DropdownToggle color="link" caret>{value}</DropdownToggle>
                : <>
                    <Button tag={Link} to={`${parentUrl}/${activeArea}`} color="link">{value}</Button>
                    <DropdownToggle color="link" split />
                </>
            }
            <DropdownMenu>{items}</DropdownMenu>
        </UncontrolledButtonDropdown>
    }, [areas, activeArea, parentUrl, t]);

    const baySelector = useMemo(() => {
        if (activeArea === undefined) {
            return null;
        }

        const items = _(bays).filter((bay) => bay !== activeBay).map((bay) => <DropdownItem key={bay} tag={Link} to={`${parentUrl}/${activeArea}/${bay}`}>{bay}</DropdownItem>).value();

        const value = activeBay === undefined ? t('Baia') : <Trans t={t} i18nKey="baySelector">Baia <strong>{activeBay}</strong></Trans>

        if (items.length === 0) {
            return activeBay === undefined ? null : <Button color="link">{value}</Button>;
        }

        return <UncontrolledButtonDropdown>
            {activeBay === undefined
                ? <DropdownToggle color="link" caret>{value}</DropdownToggle>
                : <>
                    <Button tag={Link} to={`${parentUrl}/${activeArea}/${activeBay}`} color="link">{value}</Button>
                    <DropdownToggle color="link" split />
                </>}
            <DropdownMenu>{items}</DropdownMenu>
        </UncontrolledButtonDropdown>
    }, [bays, activeBay, activeArea, parentUrl, t]);

    const rowSelector = useMemo(() => {
        if (activeArea === undefined) {
            return null;
        }

        const items = _(rows).filter((row) => row !== activeRow && row !== '').map((row) => <DropdownItem key={row} tag={Link} to={`${parentUrl}/${activeArea}/${activeBay}/${row}`}>{row}</DropdownItem>).value();

        const value = activeRow === undefined ? t('Riga') : <Trans t={t} i18nKey="rowSelector">Riga <strong>{activeRow}</strong></Trans>

        if (items.length === 0) {
            return activeRow === undefined ? null : <Button color="link">{value}</Button>;
        }

        return <UncontrolledButtonDropdown>
            {activeRow === undefined
                ? <DropdownToggle color="link" caret>{value}</DropdownToggle>
                : <>
                    <Button tag={Link} to={`${parentUrl}/${activeArea}/${activeBay}/${activeRow}`} color="link">{value}</Button>
                    <DropdownToggle color="link" split />
                </>}
            <DropdownMenu>{items}</DropdownMenu>
        </UncontrolledButtonDropdown>
    }, [rows, activeRow, activeBay, activeArea, parentUrl, t]);

    const breadcrumbs = useMemo(() => <PositionBreadcrumb>
        <BreadcrumbItem>
            <Button tag={Link} to={parentUrl} color="link">{t('Unità in sosta')}</Button>
        </BreadcrumbItem>
        {areaSelector === null ? null : <BreadcrumbItem>{areaSelector}</BreadcrumbItem>}
        {baySelector === null ? null : <BreadcrumbItem>{baySelector}</BreadcrumbItem>}
        {rowSelector === null ? null : <BreadcrumbItem>{rowSelector}</BreadcrumbItem>}
    </PositionBreadcrumb>, [parentUrl, t, areaSelector, baySelector, rowSelector])

    const [areaRiskAssessments, terminalRisk] = useMemo(() => reduce(riskAssessments, ([areaRisks, terminalRisk], risk) => {
        if (risk.area === 'TERMINAL') {
            return [areaRisks, risk]
        }
        return [concat(areaRisks, risk), terminalRisk];
        // @ts-ignore
    }, [[] as DeepPartialGraph<RiskAssessment>[], null as DeepPartialGraph<RiskAssessment>]), [riskAssessments])

    const updating = handlings.length > 0 && terminalRisk === null;

    useEffect(() => {
        const handle = setInterval(refresh, updating ? EXPIRED_REFRESH_TIME : REFRESH_TIME);

        return () => {
            clearInterval(handle);
        }
    }, [updating, refresh])

    useEffect(() => {

        const lastUpdate = dataUpdate?.lastUpdate ?? null;

        if (lastUpdate === null) {
            return;
        }

        if (updating) {
            expiredDataUpdate.current = lastUpdate;
            return;
        }

        expiredDataUpdate.current = null;
    }, [updating, dataUpdate])


    const handleAreaRowClick: TerminalAreasRiskRowAreaRowClickHandler = useCallback((area, bay, row) => {
        history.push(`${parentUrl}/${filter([area, bay, row], (s) => s !== undefined && s !== null && s.length > 0).join('/')}`);
    }, [history, parentUrl]);

    const handleHandlingRowClick = useCallback((unitNumber: string) => {
        history.push(`/${terminalSlug}/${t('unita-in-sosta')}/${unitNumber.toLowerCase()}`);
    }, [history, terminalSlug, t]);

    const areasTable = useMemo(() => {
        if (riskAssessments === undefined || dataUpdate === undefined || dataUpdate === null) {
            return null;
        }

        const lastUpdate = (dataUpdate.lastUpdate !== undefined && dataUpdate.lastUpdate !== expiredDataUpdate.current)
            ? <DateTime date={dataUpdate.lastUpdate} />
            : t('adesso');


        const header = <Navbar className="navbar ng-white border-bottom box-shadow" light>
            <div>
                <NavbarText><strong>{t('Rischio')}</strong></NavbarText>
            </div>
            <div className="d-sm-inline-flex flex-sm-row" />
            <div className="navbar-right d-sm-inline-flex flex-sm-row">
                {isSuperuser ? <Button onClick={refresh}>{t('Ricarica')}</Button> : null}
            </div>
        </Navbar>;

        return <GridLayout header={header}>
            <TerminalRiskTable className="table-sm">
                {isSuperuser ? <colgroup><col /></colgroup> : null}
                <colgroup>
                    <col />
                </colgroup>
                <colgroup>
                    <col />
                    <col />
                    <col />
                </colgroup>
                <colgroup>
                    <col />
                    <col />
                    <col />
                </colgroup>
                <colgroup>
                    <col />
                    <col />
                    <col />
                </colgroup>


                <thead>
                    <tr>
                        {isSuperuser ? <th rowSpan={2}>{t('ID')}</th> : null}
                        <th rowSpan={2} scope={'col'}>{t('Area')}</th>
                        <th colSpan={3} scope={'colGroup'}>{t('Fei')}</th>
                        <th colSpan={3} scope={'colGroup'}>{t('Cei')}</th>
                        <th colSpan={3} scope={'colGroup'}>{t('Distanza standard')}</th>
                    </tr>
                    <tr>
                        {/*spanned*/}
                        <th scope={'col'}>{t('Max')}</th>
                        <th scope={'col'}>{t('Baia')}</th>
                        <th scope={'col'}>{t('Riga')}</th>

                        <th scope={'col'}>{t('Max')}</th>
                        <th scope={'col'}>{t('Baia')}</th>
                        <th scope={'col'}>{t('Riga')}</th>

                        <th scope={'col'}>{t('Max')}</th>
                        <th scope={'col'}>{t('Baia')}</th>
                        <th scope={'col'}>{t('Riga')}</th>
                    </tr>
                </thead>
                <tbody> {map(orderBy(areaRiskAssessments, ...[['area', 'desc']]), riskAssessment => {
                    const active = riskAssessment.area === activeArea;
                    return <AreaRiskRow
                        key={riskAssessment.id}
                        riskAssessment={riskAssessment}
                        onRowClick={handleAreaRowClick}
                        active={active}
                        activeBay={active === true ? activeBay ?? '' : null}
                        activeRow={active === true ? activeRow ?? '' : null}
                        ceiLimit={ceiLimit}
                        feiLimit={feiLimit}
                        radiusLimit={radiusLimit}
                    />
                })}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={isSuperuser ? 11 : 10}>
                            {updating ? t('Aggiornamento in corso...') : <Trans>Ultimo aggiornamento {lastUpdate}</Trans>}
                        </td>
                    </tr>
                </tfoot>
            </TerminalRiskTable>
        </GridLayout>
    }, [riskAssessments, dataUpdate, t, isSuperuser, refresh, areaRiskAssessments, updating, activeArea, handleAreaRowClick, activeBay, activeRow, ceiLimit, feiLimit, radiusLimit]);

    const terminalName = terminal?.name

    const tierFormatter = useMemo(() => terminalName === 'TERMINAL DEL GOLFO' ? numericTierFromBrt : (tier: string | null | undefined) => tier?.toString() ?? "", [terminalName])

    const th = useMemo(() => {
        const [column, direction] = handlingsOrderBy;
        const headers: Record<'id' | 'unitNumber' | 'adrClasses' | 'risk' | 'position' | 'fei' | 'cei' | 'standardDistance' | 'area' | 'bay' | 'row' | 'tier', { column?: keyof Handling | null, name: string, defaultDirection?: 'asc' | 'desc' }> = {
            id: { name: t('ID') },
            unitNumber: { column: 'loadingUnitIdentifier', name: t('Sigla') },
            adrClasses: { name: t('Classi'), column: null },
            risk: { name: t('Rischio'), defaultDirection: 'desc' },
            fei: { name: t('Fei'), defaultDirection: 'desc' },
            cei: { name: t('Cei'), defaultDirection: 'desc' },
            standardDistance: { name: t('Distanza Standard'), defaultDirection: 'desc' },
            position: { name: t('Posizione') },
            area: { name: t('Area') },
            bay: { name: t('Baia') },
            row: { name: t('Riga') },
            tier: { name: t('Tiro') },
        };

        return _(headers).map(({ name, column: col, defaultDirection }, key) => {
            const newCol = col === undefined ? key as keyof Handling | 'risk' : col;

            if (newCol === null) return [key, name]

            if (newCol !== column) return [key, <a onClick={() => setHandlingsOrderBy([newCol, defaultDirection ?? 'asc'])}>{name}</a>]


            const [newDir, icon]: ['asc' | 'desc', React.ReactNode] = direction === 'asc' ? ['desc', <FaArrowDown />] : ['asc', <FaArrowUp />];

            const changeOrderBy = () => setHandlingsOrderBy([newCol, newDir]);

            return [key, <a onClick={() => changeOrderBy()}>{name} {icon}</a>]
        }).fromPairs().value() as Record<keyof typeof headers, React.ReactNode>;

    }, [handlingsOrderBy, t, setHandlingsOrderBy]);

    const adrClassCells = useMemo(() => {

        var result = new Map<string, JSX.Element>()

        var separator = ', '

        for (const { loadingUnitIdentifier, dangerousGoods } of handlings) {
            if (isNil(loadingUnitIdentifier) || isNil(dangerousGoods)) continue;

            const cell = <>{Array.from(dangerousGoods.reduce((prev, { adrClass, limitedQuantity }) => {
                if (isNil(adrClass) || isNil(limitedQuantity)) return prev;
                return prev.set(adrClass, (prev.get(adrClass) ?? true) && limitedQuantity);
            }, new Map<string, boolean>()).entries() ?? []).sort(([cl1], [cl2]) => {
                if (cl1 < cl2) return -1;
                if (cl2 > cl1) return 1;
                return 0;
            }).map(([adrClass, limitedQuantity], i, arr) => {
                const sep = i + 1 < arr.length ? separator : null
                return limitedQuantity
                    ? <><span title={t('Classe {{adrClass}}, quantità limitata', { adrClass })}>{adrClass}</span>{sep}</>
                    : <><strong title={t('Classe {{adrClass}}', { adrClass })}>{adrClass}</strong>{sep}</>
            })}</>

            result.set(loadingUnitIdentifier, cell);
        }

        return result;
    }, [handlings, t])

    const thead = useMemo(() => {
        const { id, unitNumber, adrClasses, risk, position, fei, cei, standardDistance, area, bay, row, tier } = th;

        return <thead>
            <tr>
                {isSuperuser ? <th rowSpan={2}>{id}</th> : null}
                <th rowSpan={2} scope={'col'}>{unitNumber}</th>
                <th rowSpan={2} scope={'col'}>{adrClasses}</th>
                <th colSpan={3} scope={'colGroup'}>{risk}</th>
                <th colSpan={4} scope={'colGroup'}>{position}</th>
            </tr>
            <tr>
                {/*spanned*/}

                <th scope={'col'}>{fei}</th>
                <th scope={'col'}>{cei}</th>
                <th scope={'col'}>{standardDistance}</th>

                <th scope={'col'}>{area}</th>
                <th scope={'col'}>{bay}</th>
                <th scope={'col'}>{row}</th>
                <th scope={'col'}>{tier}</th>
            </tr>
        </thead>
    }, [isSuperuser, th]);

    const details = useMemo(() => {
        if (terminal === undefined || terminal === null) {
            return null;
        }

        const activeHandlings = activeArea === undefined
            ? handlings
            : filter(handlings, activeRow !== undefined
                ? { area: activeArea, bay: activeBay, row: activeRow }
                : activeBay !== undefined
                    ? { area: activeArea, bay: activeBay }
                    : ['area', activeArea]);


        return <GridLayout header={breadcrumbs}>
            <Helmet>
                <title>{t(`Rischio ${_([activeArea, activeBay, activeRow]).filter((a) => a !== undefined).join('/')}`)} :: {terminal.name} :: {t('app.title')}</title>
            </Helmet>
            <UnitsRiskTable striped className="table-sm">
                {thead}
                <tbody>
                    {map(activeHandlings, ({ id, loadingUnitIdentifier, fei, cei, standardDistance, area, bay, row, tier, dangerousGoods }) =>
                        <tr key={id} onClick={(ev) => ev.isDefaultPrevented() ? true : handleHandlingRowClick(loadingUnitIdentifier as string)}>
                            {isSuperuser ? <th scope="row">{id}</th> : null}
                            <th scope="row"><LoadingUnitIdentifier identifier={loadingUnitIdentifier} /></th>

                            <td>{adrClassCells.get(loadingUnitIdentifier ?? '') ?? '-'}</td>

                            <td><RiskValue value={fei} limit={feiLimit} /></td>
                            <td><RiskValue value={cei} limit={ceiLimit} /></td>
                            <td><RiskValue value={standardDistance} limit={radiusLimit} format={(v) => `${v}m`} /></td>

                            <td>{area === undefined || area === null ? null : <Link onClick={ev => ev.stopPropagation()} to={`${parentUrl}/${area}`}>{area}</Link>}</td>
                            <td>{bay === undefined || bay === null ? null : <Link onClick={ev => ev.stopPropagation()} to={`${parentUrl}/${area}/${bay}`}>{bay}</Link>}</td>
                            <td>{row === undefined || row === null ? null : <Link onClick={ev => ev.stopPropagation()} to={`${parentUrl}/${area}/${bay}/${row}`}>{row}</Link>}</td>
                            <td>{tierFormatter(tier)}</td>
                        </tr>
                    )}
                </tbody>
            </UnitsRiskTable>
        </GridLayout>;
    }, [terminal, activeArea, handlings, activeRow, activeBay, breadcrumbs, t, thead, isSuperuser, adrClassCells, feiLimit, ceiLimit, radiusLimit, parentUrl, tierFormatter, handleHandlingRowClick]);

    if (fetching && terminal === undefined) {
        return <Loading />
    }

    if (handlings.length === 0) {
        return <CenteredContainer>{t('Nessuna unità in sosta')}</CenteredContainer>
    }

    return <GridLayout left={areasTable}>{details}</GridLayout>;
};

type TerminalAreasRiskPageProps = {
    id: number
}

const TerminalAreasRiskPage: FunctionComponent<TerminalAreasRiskPageProps> = ({ id }) => {
    // @ts-ignore
    const { state, ...location } = useLocation<{ parentUrl: string, terminal: DeepPartialGraph<Terminal> }>();

    const { url } = useRouteMatch();

    return <PrivateRoute location={{ state: { ...(state ?? {}), parentUrl: url }, ...location }} path={[`${url}/:area/:bay/:row`, `${url}/:area/:bay`, `${url}/:area`, `${url}`]}>
        <TerminalAreasRisk id={id} />
    </PrivateRoute>;
}

export default TerminalAreasRiskPage;